import { KeyValue } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, Observable, share, throwError } from 'rxjs';
import { GainsHttpParamEncoder } from './gains-http-param-encoder';
import { FilterModel } from 'ag-grid-community';
import { ErrorContextService } from '../../shared/services/error-context/error-context.service';

export class HttpClientWrapper {
  private _queryParams: HttpParams;
  private readonly _pathParams: KeyValue<any, any>[];
  private _urlPath?: string;
  private _requestBodyObj: any;
  private _responseType = 'json';
  constructor(
    private readonly baseUrl: string,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly errorContext?: ErrorContextService
  ) {
    this._queryParams = new HttpParams({ encoder: new GainsHttpParamEncoder() });
    this._pathParams = [];
  }

  url(url: string) {
    this._urlPath = url;
    return this;
  }
  queryParam(key: string, value?: string | number | boolean | null) {
    if (value !== undefined && value !== null && value !== '') {
      this._queryParams = this._queryParams.append(key, String(value));
    }
    return this;
  }
  pathParam(key: string, value: string) {
    this._pathParams.push({
      key,
      value: encodeURIComponent(value)
    });
    return this;
  }

  /**
   * Transform and add filter model as query parameter
   * Handles special transformations for set filters and inRange types
   */
  filterModelQueryParam(value: FilterModel) {
    const transformedFilter = this.transformFilterModel(value);
    return this.queryParam('filter', JSON.stringify(transformedFilter));
  }

  /**
   * Generic filter transformation logic
   * - Transforms 'set' filterType to 'text'/'in' format
   * - Transforms 'inRange' type to conditions structure
   */
  private transformFilterModel(filter: any): any {
    const newFilter = { ...filter };

    // Process each field in the filter
    Object.keys(newFilter).forEach(fieldName => {
      const fieldFilter = newFilter[fieldName];

      // Handle 'set' filterType (e.g., mlFlag)
      if (fieldFilter?.filterType === 'set' && fieldFilter?.values) {
        const values = fieldFilter.values;
        const transformedValues = values.map((v: string) => (v === 'true' ? '1' : '0'));

        newFilter[fieldName] = {
          filter: transformedValues.join(','),
          filterType: 'text',
          type: 'in'
        };
      }

      // Handle 'inRange' type for numeric fields
      if (fieldFilter?.type === 'inRange') {
        const filterValue = fieldFilter.filter;
        const filterTo = fieldFilter.filterTo;
        newFilter[fieldName] = {
          filterType: 'number',
          operator: 'AND',
          conditions: [
            {
              filterType: 'number',
              type: 'greaterThanOrEqual',
              filter: filterValue
            },
            {
              filterType: 'number',
              type: 'lessThanOrEqual',
              filter: filterTo
            }
          ]
        };
      }
    });

    return newFilter;
  }

  get<T>(): Observable<T> {
    const url = this.buildUrl();
    return this.http
      .get<T>(url, {
        params: this._queryParams,
        responseType: this._responseType as 'json'
      })
      .pipe(
        catchError(err => {
          this.handleError(err);
          return throwError(() => err);
        }),
        share()
      );
  }
  requestBody(requestBodyObj: any) {
    this._requestBodyObj = requestBodyObj;
    return this;
  }
  post<T>(): Observable<T> {
    return this.withErrorHandling(this.http.post<T>(this.buildUrl(), this._requestBodyObj));
  }
  put<T>(): Observable<T> {
    return this.withErrorHandling(this.http.put<T>(this.buildUrl(), this._requestBodyObj));
  }
  patch<T>(): Observable<T> {
    return this.withErrorHandling(this.http.patch<T>(this.buildUrl(), this._requestBodyObj));
  }
  delete<T>(): Observable<T> {
    return this.withErrorHandling(this.http.delete<T>(this.buildUrl(), { params: this._queryParams, body: this._requestBodyObj }));
  }
  setResponseType(responseType: string) {
    this._responseType = responseType;
    return this;
  }
  private buildUrl() {
    let url = this.baseUrl + this._urlPath;
    for (const item of this._pathParams) {
      url = url.replace(`{${item.key}}`, item.value);
    }
    return url;
  }
  private withErrorHandling<T>(request: Observable<T>): Observable<T> {
    return request.pipe(
      catchError(err => {
        this.handleError(err);
        return throwError(() => err);
      }),
      share()
    );
  }
  private handleError(error: any): void {
    if (this.isAuthenticationError(error)) {
      this.router.navigate(['/login']);
      return;
    }
    if (this.isServerError(error)) {
      this.redirectToErrorPage();
    }
  }

  private isAuthenticationError(error: any): boolean {
    return !!error && error.status === 401;
  }

  private isServerError(error: any): boolean {
    return !!error && typeof error.status === 'number' && error.status >= 500;
  }

  /**
   * Sends the user to the generic error page on a server (5xx) failure. Only the originating route
   * is recorded — never the raw error message, so internal errors are not exposed (PLAN-2069).
   */
  private redirectToErrorPage(): void {
    this.errorContext?.recordError(this.router.url);
    this.router.navigate(['/error']);
  }
}
